import React from "react";
import { Icon } from "../../../../designSystem/icons/Icons";
import { SurveyTxt } from "../../../../SurveyTxt";
import styled from "styled-components";
import { colors } from "../../../../designSystem/colors/color";
import { Flex } from "../../../../designSystem/compositeComponents/FlexHelpers";
import { InfoChip } from "../../../../designSystem/Chip";
import { SECONDS_PER_QUESTION } from "./SurveyAnswerTypes";

type IntroductionProgressPanelProps = {
    hasSegmentations: boolean;
    missingQuestionCount:number;
    txt: SurveyTxt;
}
export function IntroductionProgressPanel(props: IntroductionProgressPanelProps): JSX.Element {
    
    const segmentationFactor = props.hasSegmentations ? 1 : 0
    const remainingTimeInMinutes = Math.ceil((props.missingQuestionCount + segmentationFactor) * SECONDS_PER_QUESTION / 60);
    
    return (
        <ProgressPanel
            stageText={props.txt.progressPanelStage_introduction_header(props.hasSegmentations)}
            progressBar={undefined}
            remainingTimeInMinutes={remainingTimeInMinutes}
        />
    );
}

type SegmentationProgressPanelProps = {
    questionCount:number;
    txt: SurveyTxt;
}
export function SegmentationProgressPanel(props: SegmentationProgressPanelProps): JSX.Element {
    
    const remainingTimeInMinutes = Math.ceil(props.questionCount * SECONDS_PER_QUESTION / 60);
    
    return (
        <ProgressPanel
            stageText={props.txt.progressPanelStage_segmentations_header}
            progressBar={{
                currentStep: 0,
                totalSteps: props.questionCount,
                completionPercentage: 0,
            }}
            remainingTimeInMinutes={remainingTimeInMinutes}
        />
    );
}

type QuestionProgressPanelProps = {
    hasSegmentations: boolean;
    currentQuestionIndex: number;
    questionCount: number;
    txt: SurveyTxt;
}
export function QuestionProgressPanel(props: QuestionProgressPanelProps): JSX.Element {
    
    const segmentOffset = props.hasSegmentations ? 1 : 0;
    const currentStep = props.currentQuestionIndex + segmentOffset;
    const totalSteps = props.questionCount + segmentOffset;

    const remainingTimeInMinutes = Math.ceil((totalSteps - currentStep) * SECONDS_PER_QUESTION / 60);
    const completionPercentage = Math.round((currentStep) / totalSteps * 100);

    return (
        <ProgressPanel
            stageText={props.txt.progressPanelStage_questions(props.hasSegmentations)}
            progressBar={{ 
                currentStep: currentStep, 
                totalSteps: totalSteps,
                completionPercentage: completionPercentage 
            }}
            remainingTimeInMinutes={remainingTimeInMinutes}
        />
    );
}

type ProgressPanelProps = {
    stageText:string;
    stepText?:string;
    progressBar?:{ currentStep: number, totalSteps: number, completionPercentage?: number };
    remainingTimeInMinutes: number;
}
function ProgressPanel(props: ProgressPanelProps): JSX.Element {
    
    const wrapperDivRef = React.useRef(null);

    return (
        <div style={{display: 'flex', flexDirection: 'column', width: '100%', marginTop:'40px', gap: '8px'}}>

            <Flex justifyContent='space-between'>
                <Flex justifyContent='flex-start'>
                    <InfoChip variant="filled-light">{props.stageText}</InfoChip>
                    {props.stepText && 
                        <ProgressText>{props.stepText}</ProgressText>
                    }
                </Flex>

                <Flex alignItems='center'>
                    <Icon name="clock" size="x-small" />
                    <ProgressText>~ {props.remainingTimeInMinutes} min. tilbage</ProgressText>
                </Flex>
            </Flex>

            {props.progressBar &&
                <>
                    <div ref={wrapperDivRef} style={{ width: '100%' }}>
                        <ProgressBarContainer>
                            <ProgressBarFill style={{ width: `${(props.progressBar.currentStep) / props.progressBar.totalSteps * 100}%` }} />
                        </ProgressBarContainer>
                    </div>

                    {props.progressBar.completionPercentage !== undefined && (
                        <Flex justifyContent='space-between'>
                            <ProgressText>{
                                Math.max(0, Math.min(100, props.progressBar.completionPercentage)).toFixed(0)}% gennemført</ProgressText>
                        </Flex>
                    )}
                </>
            }

        </div>
    );
}

const ProgressText = styled.span`
    color: #444;
    font-size: 14px;
    font-weight: 500;
`

const ProgressBarContainer = styled.div`
    background-color: #e6e6e6;
    border-radius: 8px;
`;
const ProgressBarFill = styled.div`
    background-color: ${colors.graphs.progressBar};
    height:16px;
    border-radius: 8px;
`;